import { useState, useEffect } from "react";
import FacilityList from "./components/FacilityList";
import PrefectureSelect from "./components/PrefectureSelect";
import KeywordInput from "./components/KeywordInput";
import OptionSelect from "./components/OptionSelect";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBookmark, faXmark } from "@fortawesome/free-solid-svg-icons";
import styles from "./App.module.css";
import JapanTable from "./components/JapanTable";
import { CSSTransition } from "react-transition-group";
import fade from "./components/fade.module.scss";

// todo: 医療機関のデータが更新されて増減があるとブックマークがずれるので、ハッシュ値を使ってデータの整合性を確認する

// データをlocalStorageに保存する関数
const saveToLocalStorage = (key, value) => {
  try {
    const serializedValue = JSON.stringify(value);
    localStorage.setItem(key, serializedValue);
  } catch (err) {
    console.error("Error saving to localStorage", err);
  }
};

// localStorageからデータを読み込む関数
const loadFromLocalStorage = (key) => {
  try {
    const serializedValue = localStorage.getItem(key);
    if (serializedValue === null) {
      return undefined;
    }
    return JSON.parse(serializedValue);
  } catch (err) {
    console.error("Error loading from localStorage", err);
    return undefined;
  }
};

function App() {
  const { t } = useTranslation();
  const [prefecture, setPrefecture] = useState("");
  const [facilities, setFacilities] = useState([]);
  const [dataHash, setDataHash] = useState(
    () => loadFromLocalStorage("dataHash") || ""
  );
  const [bookmarkedFacilities, setBookmarkedFacilities] = useState(
    () => loadFromLocalStorage("bookmarkedFacilities") || []
  );
  const [showBookmark, setShowBookmark] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [filter, setFilter] = useState({
    keyword: "",
    options: { course: "", laday: false },
  });

  useEffect(() => {
    async function fetchData() {
      try {
        const dataUrl = process.env.REACT_APP_DATA_URL;
        const response = await fetch(dataUrl);
        const data = await response.json();
        const processedData = data.map((item) => {
          // "day_" または "night_" で始まる属性をチェックする
          const hasDayOrNightData = Object.keys(item).some(
            (key) =>
              (key.startsWith("day_") ||
                key.startsWith("night_") ||
                key.startsWith("kenporen_")) &&
              !key.endsWith("_l") &&
              item[key] !== ""
          )
            ? "〇"
            : "";

          const hasLadyData = Object.keys(item).some(
            (key) => key.endsWith("_l") && item[key] !== ""
          )
            ? "〇"
            : "";

          // 条件に応じて新しい属性 D を追加
          return {
            ...item,
            D: hasDayOrNightData,
            L: hasLadyData,
          };
        });

        setFacilities(processedData);

        // calc hash
        const newHash = await calculateSHA256Hash(data);
        const oldHash = loadFromLocalStorage("dataHash");

        if (newHash !== oldHash) {
          setBookmarkedFacilities([]);
          saveToLocalStorage("bookmarkedFacilities", []); // リセットしたブックマーク情報をlocalStorageに保存
        }

        setDataHash(newHash);
      } catch (error) {
        console.log(error);
      }
    }
    fetchData();
  }, []);

  useEffect(() => {
    saveToLocalStorage("dataHash", dataHash);
  }, [dataHash]);

  useEffect(() => {
    saveToLocalStorage("bookmarkedFacilities", bookmarkedFacilities);
    if (bookmarkedFacilities.length === 0) {
      setShowBookmark(false);
    }
  }, [bookmarkedFacilities]);

  const handleFilterChange = (key, value) => {
    setFilter((prev) => ({ ...prev, [key]: value }));
  };

  const handlePrefectureSelect = (value) => {
    setPrefecture(value);
  };

  const handleBookmark = (facilityId, flag) => {
    setBookmarkedFacilities((prev) => {
      if (flag) {
        return [...prev, facilityId];
      } else {
        return prev.filter((id) => id !== facilityId);
      }
    });
  };

  const handleShowBookmark = () => {
    setShowBookmark(!showBookmark);
  };

  async function calculateSHA256Hash(jsonData) {
    // JSONデータを文字列に変換
    const jsonString = JSON.stringify(jsonData);

    // 文字列をUint8Array（バイト配列）に変換
    const encoder = new TextEncoder();
    const data = encoder.encode(jsonString);

    // SHA-256ハッシュを計算
    const hashBuffer = await crypto.subtle.digest("SHA-256", data);

    // ハッシュ値を16進数の文字列に変換
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
    return hashHex;
  }

  const filteredFacilities = facilities.filter((facility) => {
    if (bookmarkedFacilities.length >= 0 && showBookmark) {
      return bookmarkedFacilities.includes(facility.id);
    } else if (filter.area === false) {
      return [];
    } else {
      const combinedAddress = facility.pref + facility.address;
      return (
        (prefecture.length === 0 || prefecture.includes(facility.pref)) &&
        (filter.keyword === "" ||
          facility.address.includes(filter.keyword) ||
          combinedAddress.includes(filter.keyword) ||
          facility.name.includes(filter.keyword)) &&
        (filter.options.course === "" || facility[filter.options.course] !== "")
      );
    }
  });

  const activePrefecture = new Set(
    filteredFacilities.map((facility) => facility.pref)
  );

  const onMapChange = () => {
    setShowMap(!showMap);
  };

  return (
    <div className={styles.container}>
      <CSSTransition
        in={!showMap}
        timeout={200}
        classNames={fade}
        unmountOnExit
      >
        <PrefectureSelect
          onPrefectureSelect={handlePrefectureSelect}
          activePrefecture={activePrefecture}
          onMapChange={onMapChange}
        />
      </CSSTransition>
      <OptionSelect
        onChange={(value) => handleFilterChange("options", value)}
      />
      <CSSTransition in={showMap} timeout={200} classNames={fade} unmountOnExit>
        <JapanTable
          onPrefectureSelect={handlePrefectureSelect}
          onMapChange={onMapChange}
          activePrefecture={activePrefecture}
        />
      </CSSTransition>
      <div className={styles.resultHeader}>
        <KeywordInput
          onChange={(value) => handleFilterChange("keyword", value)}
        />
        {facilities.length !== filteredFacilities.length ? (
          <div
            style={{
              display: filteredFacilities.length > 0 ? "block" : "none",
            }}
          >
            {t("{{count}}件の施設があります。", {
              count: filteredFacilities.length,
            })}
          </div>
        ) : (
          ""
        )}
        <div
          style={{
            display: filteredFacilities.length === 0 ? "block" : "none",
          }}
        >
          {t("条件に該当する施設が見つかりませんでした。")}
        </div>
        {bookmarkedFacilities.length > 0 && (
          <div className={styles.showBookmarkButton}>
            <button onClick={handleShowBookmark}>
              <span className={styles.faIcon}>
                <FontAwesomeIcon icon={showBookmark ? faXmark : faBookmark} />
              </span>
              {t("ブックマークを表示")}({bookmarkedFacilities.length})
            </button>
          </div>
        )}
      </div>
      <div className={styles.alertBox}>
        受診日は1カ月以上の余裕をもって決定してください。
      </div>

      {facilities.length !== filteredFacilities.length ? (
        <div>
          <FacilityList
            filteredFacilities={filteredFacilities}
            handleBookmark={handleBookmark}
            bookmarkedFacilities={bookmarkedFacilities}
          />
        </div>
      ) : (
        ""
      )}
    </div>
  );
}

export default App;
